import React, { useState, useEffect, useCallback } from 'react';
import { fetchWatch1Data as fetchWatch1Api } from '../services/api';

/**
 * 감시1 탭 컴포넌트
 * HAITIAN 사출기 감시1 화면을 구현
 */
const MonitoringWatch1 = ({ equipmentId }) => {
  const [autoWatch, setAutoWatch] = useState('사용');
  const [referenceType, setReferenceType] = useState('자동샘플링+사용자설정');
  const [loading, setLoading] = useState(false);

  // 테이블1 데이터 (형폐, 금형보호, 고압, 형개, 사출완료, 행정시간, 압출, 형개완료)
  // 초기값을 mockWatch1Data와 동일하게 설정하여 깜빡임 방지
  const [table1Data, setTable1Data] = useState({
    columns: [
      { key: 'moldClose', name: '형폐', unit: 'sec' },
      { key: 'moldProtect', name: '금형보호', unit: 'sec' },
      { key: 'highPressure', name: '고압', unit: 'sec' },
      { key: 'moldOpen', name: '형개', unit: 'sec' },
      { key: 'injectionComplete', name: '사출완료', unit: 'mm' },
      { key: 'cycleTime', name: '행정시간', unit: 'sec' },
      { key: 'extrusion', name: '압출', unit: 'sec' },
      { key: 'moldOpenComplete', name: '형개완료', unit: 'mm' }
    ],
    rows: {
      actual: [2.68, 0.70, 0.39, 2.86, 10.3, 21.32, 3.96, 261.6],
      deviationPercent: [null, null, null, null, null, null, null, null],
      deviationAmount: [3.24, 3.33, 2.95, 1.93, 7.6, 15.57, 5.50, 44.0],
      reference: [2.92, 1.03, 0.34, 1.79, 7.9, 29.89, 4.46, 295.6],
      upperLimit: [5.92, 4.03, 3.34, 4.79, 17.9, 36.89, 9.46, 305.6],
      prevShot: [2.67, 0.70, 0.38, 2.90, 57.0, 29.04, 3.95, 261.6],
      lowerLimit: [0.00, 0.00, 0.00, 0.00, 0.0, 22.89, 0.00, 285.6],
      selected: [true, false, false, false, true, true, false, true]
    }
  });

  // 테이블2 데이터 (사출최대압, 사출보압, 보압전환mm, 보압전환sec, 보압전환Bar, 쿠션양, 사출시작, 계량, 석백시간)
  // 초기값을 mockWatch1Data 및 API 응답과 동일하게 설정하여 깜빡임 방지
  const [table2Data, setTable2Data] = useState({
    columns: [
      { key: 'maxInjectionPressure', name: '사출최대압', unit: 'Bar' },
      { key: 'holdingPressure', name: '사출보압', unit: 'sec' },
      { key: 'holdingTransferMm', name: '보압전환', unit: 'mm' },
      { key: 'holdingTransferSec', name: '보압전환', unit: 'sec' },
      { key: 'holdingTransferBar', name: '보압전환', unit: 'Bar' },
      { key: 'cushionAmount', name: '쿠션양', unit: 'mm', important: true },
      { key: 'injectionStart', name: '사출시작', unit: 'mm' },
      { key: 'metering', name: '계량', unit: 'sec', important: true },
      { key: 'suckbackTime', name: '석백시간', unit: 'sec' }
    ],
    rows: {
      actual: [76, 5.30, 8.4, 2.00, 59, 7.6, 34.1, 3.10, 0.57],
      deviationPercent: [null, null, null, null, null, null, null, null, null],
      deviationAmount: [9.0, 5.90, 8.3, 3.00, 6.0, 0.9, 48.8, 3.77, 2.78],
      reference: [85.0, 8.20, 11.8, 2.00, 48.0, 8.2, 32.6, 3.73, 0.35],
      upperLimit: [85.0, 11.20, 16.7, 5.00, 53.0, 8.5, 82.9, 6.87, 3.35],
      prevShot: [76, 5.30, 8.4, 2.00, 60, 7.6, 34.0, 3.11, 0.57],
      lowerLimit: [79.0, 5.20, 6.7, 0.00, 43.0, 7.5, 0.0, 0.87, 0.00],
      selected: [false, false, false, false, false, true, true, true, false]
    }
  });

  // 감시1 전용 API 호출
  const fetchWatch1Data = useCallback(async () => {
    if (!equipmentId) return;

    setLoading(true);
    try {
      const data = await fetchWatch1Api(equipmentId);
      if (data.success && data.data) {
        const { current, prevShot } = data.data;

        if (current) {
          // 테이블1 실제값 맵핑
          const table1Actual = [
            current.moldCloseTime || 0,      // 형폐 (sec)
            current.moldProtectTime || 0,    // 금형보호 (sec)
            current.highPressureTime || 0,   // 고압 (sec)
            current.moldOpenTime || 0,       // 형개 (sec)
            current.injectionTime || 0,      // 사출완료 (sec)
            current.cycleTime || 0,          // 행정시간 (sec)
            current.ejectTime || 0,          // 압출 (sec)
            current.moldOpenPosition || 0    // 형개완료 (mm)
          ];

          // 테이블1 전쇼트 맵핑
          const table1PrevShot = prevShot ? [
            prevShot.moldCloseTime || 0,
            prevShot.moldProtectTime || 0,
            prevShot.highPressureTime || 0,
            prevShot.moldOpenTime || 0,
            prevShot.injectionTime || 0,
            prevShot.cycleTime || 0,
            prevShot.ejectTime || 0,
            prevShot.moldOpenPosition || 0
          ] : table1Actual;

          // 테이블1 참고값 (PM-Quality 기준)
          // 형폐, 금형보호, 고압, 형개, 사출완료, 행정시간, 압출, 형개완료
          const table1Reference = [2.92, 1.03, 0.34, 1.79, 7.9, 29.89, 4.46, 295.6];
          const table1Upper = [5.92, 4.03, 3.34, 4.79, 17.9, 36.89, 9.46, 305.6];
          const table1Lower = [0.00, 0.00, 0.00, 0.00, 0.0, 22.89, 0.00, 285.6];

          // 편차% - 모두 "-" 표시
          const table1DeviationPercent = table1Actual.map(() => null);

          // 편차량 계산 (|상한 - 실제값|)
          const table1DeviationAmount = table1Actual.map((actual, idx) => {
            return Math.abs(table1Upper[idx] - actual);
          });

          setTable1Data(prev => ({
            ...prev,
            rows: {
              ...prev.rows,
              actual: table1Actual,
              reference: table1Reference,
              upperLimit: table1Upper,
              lowerLimit: table1Lower,
              prevShot: table1PrevShot,
              deviationPercent: table1DeviationPercent,
              deviationAmount: table1DeviationAmount
            }
          }));

          // 테이블2 실제값 맵핑
          const table2Actual = [
            current.injMaxPressure || 0,     // 사출최대압 (Bar)
            current.holdPressure || 0,       // 사출보압 (Bar)
            current.turnPosition || 0,       // 보압전환 위치 (mm)
            current.turnTime || 0,           // 보압전환 시간 (sec)
            current.turnPressure || 0,       // 보압전환 압력 (Bar)
            current.cushionAmount || 0,      // 쿠션양 (mm)
            current.injStartPosition || 0,   // 사출시작 (mm)
            current.meteringPosition || 0,   // 계량 (mm)
            current.suckbackTime || 0        // 석백시간 (sec)
          ];

          // 테이블2 전쇼트 맵핑
          const table2PrevShot = prevShot ? [
            prevShot.injMaxPressure || 0,
            prevShot.holdPressure || 0,
            prevShot.turnPosition || 0,
            prevShot.turnTime || 0,
            prevShot.turnPressure || 0,
            prevShot.cushionAmount || 0,
            prevShot.injStartPosition || 0,
            prevShot.meteringPosition || 0,
            prevShot.suckbackTime || 0
          ] : table2Actual;

          // 테이블2 참고값 (PM-Quality 기준)
          // 사출최대압, 사출보압, 보압전환(mm), 보압전환(sec), 보압전환(Bar), 쿠션양, 사출시작, 계량, 석백시간
          const table2Reference = [85.0, 8.20, 11.8, 2.00, 48.0, 8.2, 32.6, 3.73, 0.35];
          const table2Upper = [85.0, 11.20, 16.7, 5.00, 53.0, 8.5, 82.9, 6.87, 3.35];
          const table2Lower = [79.0, 5.20, 6.7, 0.00, 43.0, 7.5, 0.0, 0.87, 0.00];

          // 편차% - 모두 "-" 표시
          const table2DeviationPercent = table2Actual.map(() => null);

          // 편차량 계산 (|상한 - 실제값|)
          const table2DeviationAmount = table2Actual.map((actual, idx) => {
            return Math.abs(table2Upper[idx] - actual);
          });

          setTable2Data(prev => ({
            ...prev,
            rows: {
              ...prev.rows,
              actual: table2Actual,
              reference: table2Reference,
              upperLimit: table2Upper,
              lowerLimit: table2Lower,
              prevShot: table2PrevShot,
              deviationPercent: table2DeviationPercent,
              deviationAmount: table2DeviationAmount
            }
          }));
        }
      }
    } catch (error) {
      console.error('감시1 데이터 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  // 데이터 조회 및 주기적 갱신
  useEffect(() => {
    fetchWatch1Data();
    const interval = setInterval(fetchWatch1Data, 3000); // 3초마다 갱신
    return () => clearInterval(interval);
  }, [fetchWatch1Data]);

  // 선택 토글 핸들러
  const handleSelectionToggle = (tableNum, colIndex) => {
    if (tableNum === 1) {
      setTable1Data(prev => {
        const newSelected = [...prev.rows.selected];
        newSelected[colIndex] = !newSelected[colIndex];
        return {
          ...prev,
          rows: { ...prev.rows, selected: newSelected }
        };
      });
    } else {
      setTable2Data(prev => {
        const newSelected = [...prev.rows.selected];
        newSelected[colIndex] = !newSelected[colIndex];
        return {
          ...prev,
          rows: { ...prev.rows, selected: newSelected }
        };
      });
    }
  };

  // 값이 상한/하한을 벗어났는지 확인
  const isOutOfRange = (actual, upper, lower) => {
    if (actual === null || actual === undefined) return false;
    return actual > upper || actual < lower;
  };

  // 테이블 렌더링
  const renderTable = (tableData, tableNum, showRowLabel = true) => {
    const { columns, rows } = tableData;
    const rowLabels = [
      { key: 'actual', label: '실제값', color: '#FFFFFF' },
      { key: 'deviationPercent', label: '편차%', color: '#9CA3AF' },
      { key: 'deviationAmount', label: '편차량', color: '#FCD34D' },
      { key: 'reference', label: '참고값', color: '#FFFFFF' },
      { key: 'upperLimit', label: '상한', color: '#EF4444' },
      { key: 'prevShot', label: '전쇼트', color: '#FFFFFF' },
      { key: 'lowerLimit', label: '하한', color: '#3B82F6' },
      { key: 'selected', label: '선택', color: '#FFFFFF' }
    ];

    const totalColumns = showRowLabel ? columns.length + 1 : columns.length;
    const colWidth = `${100 / totalColumns}%`;

    return (
      <div style={{ overflowX: 'auto', height: '100%' }}>
        <table style={{
          width: '100%',
          height: '100%',
          borderCollapse: 'separate',
          borderSpacing: '3px 0',
          fontSize: '13px',
          tableLayout: 'fixed'
        }}>
          <thead>
            <tr>
              {showRowLabel && (
                <th style={{
                  padding: '8px 6px',
                  backgroundColor: '#1B1D20',
                  color: '#9CA3AF',
                  fontWeight: '600',
                  textAlign: 'center',
                  width: colWidth,
                  position: 'sticky',
                  left: 0,
                  zIndex: 1
                }}>
                  구분
                </th>
              )}
              {columns.map((col, idx) => (
                <th key={idx} style={{
                  padding: '8px 6px',
                  backgroundColor: '#1B1D20',
                  color: '#FFFFFF',
                  fontWeight: '600',
                  borderTop: col.important ? '2px dashed #F472B6' : 'none',
                  borderLeft: col.important ? '2px dashed #F472B6' : 'none',
                  borderRight: col.important ? '2px dashed #F472B6' : 'none',
                  textAlign: 'center',
                  width: colWidth,
                  whiteSpace: 'nowrap'
                }}>
                  {col.name}
                  <div style={{ fontSize: '10px', color: '#9CA3AF' }}>
                    ({col.unit})
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowLabels.map((rowInfo, rowIdx) => (
              <tr key={rowInfo.key}>
                {showRowLabel && (
                  <td style={{
                    padding: '6px',
                    color: rowInfo.color,
                    fontWeight: '500',
                    textAlign: 'center',
                    backgroundColor: '#1B1D20',
                    width: colWidth,
                    position: 'sticky',
                    left: 0,
                    zIndex: 1
                  }}>
                    {rowInfo.label}
                  </td>
                )}
                {columns.map((col, colIdx) => {
                  const value = rows[rowInfo.key][colIdx];
                  const isActualRow = rowInfo.key === 'actual';
                  const outOfRange = isActualRow && isOutOfRange(
                    rows.actual[colIdx],
                    rows.upperLimit[colIdx],
                    rows.lowerLimit[colIdx]
                  );
                  const isSelectedRow = rowInfo.key === 'selected';
                  const isImportant = col.important;

                  const isLastRow = rowInfo.key === 'selected';
                  return (
                    <td key={colIdx} style={{
                      padding: '6px',
                      color: outOfRange ? '#FFFFFF' : rowInfo.color,
                      backgroundColor: outOfRange ? 'rgba(220, 38, 38, 0.6)' : '#2a2d32',
                      borderBottom: isImportant && isLastRow ? '2px dashed #F472B6' : '1px solid #1B1D20',
                      borderLeft: isImportant ? '2px dashed #F472B6' : 'none',
                      borderRight: isImportant ? '2px dashed #F472B6' : 'none',
                      textAlign: 'center',
                      fontWeight: isActualRow ? '600' : '400'
                    }}>
                      {isSelectedRow ? (
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleSelectionToggle(tableNum, colIdx)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#10B981'
                          }}
                        />
                      ) : (
                        value !== null && value !== undefined ?
                          (typeof value === 'number' ? value.toFixed(2) : value) :
                          '-'
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      height: '100%'
    }}>
      {/* 설정 카드 */}
      <div style={{
        backgroundColor: '#21252b',
        border: '1px solid #1B1D20',
        padding: '4px 12px'
      }}>
        <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ color: '#9CA3AF', fontSize: '13px' }}>자동감시:</label>
            <select
              value={autoWatch}
              onChange={(e) => setAutoWatch(e.target.value)}
              style={{
                padding: '6px 10px',
                backgroundColor: '#1B1D20',
                border: '1px solid #374151',
                borderRadius: '4px',
                color: '#E5E7EB',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              <option value="사용">사용</option>
              <option value="미사용">미사용</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ color: '#9CA3AF', fontSize: '13px' }}>참고값:</label>
            <select
              value={referenceType}
              onChange={(e) => setReferenceType(e.target.value)}
              style={{
                padding: '6px 10px',
                backgroundColor: '#1B1D20',
                border: '1px solid #374151',
                borderRadius: '4px',
                color: '#E5E7EB',
                fontSize: '13px',
                cursor: 'pointer',
                minWidth: '180px'
              }}
            >
              <option value="자동샘플링+사용자설정">자동샘플링+사용자설정</option>
              <option value="자동샘플링">자동샘플링</option>
              <option value="사용자설정">사용자설정</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#DC2626', borderRadius: '2px' }}></div>
              <span style={{ color: '#6B7280', fontSize: '11px' }}>상/하한 초과</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#3d2929', border: '1px dashed #F472B6', borderRadius: '2px' }}></div>
              <span style={{ color: '#6B7280', fontSize: '11px' }}>중요 감시</span>
            </div>
          </div>
        </div>
      </div>

      {/* 테이블 1 카드 */}
      <div style={{
        backgroundColor: '#21252b',
        border: '1px solid #1B1D20',
        padding: '12px',
        flex: 1
      }}>
        {renderTable(table1Data, 1)}
      </div>

      {/* 테이블 2 카드 */}
      <div style={{
        backgroundColor: '#21252b',
        border: '1px solid #1B1D20',
        padding: '12px',
        flex: 1
      }}>
        {renderTable(table2Data, 2, false)}
      </div>
    </div>
  );
};

export default MonitoringWatch1;
